import "./theme/globals.css";
import { lazy, type PropsWithChildren, Suspense, useMemo } from "react";
import { Route, Routes } from "react-router-dom-v5-compat";
import { Funnel } from "@b2bportal/core-types";
import { LazyLoading } from "@b2bportal/core-ui";
import { HOTELS_PATH } from "@b2bportal/core-utilities";
import { ExperimentsProvider } from "@hopper-b2b/experiments";
import { Portal } from "@hopper-b2b/portal";
import { AgentImpersonation } from "@hopper-b2b/self-serve";
import { SlotProvider, type Slots } from "@hopper-b2b/ui";
import {
  apiConfig,
  PATH_AGENT_IMPERSONATION,
  UserSourceProvider,
} from "@hopper-b2b/utilities";
import config, { environmentConfig } from "../config";
import "./App.scss";
import { AppTrackingProvider, AxiosInterceptorWrapper } from "./components";
import {
  InvalidSessionPage,
  StartSessionComponent,
} from "./components/AuthModule/components";
import {
  SessionContextProvider,
  useSessionContext,
} from "./components/AuthModule/SessionContext";
import { CommerceTripsRoute } from "./components/CommerceTripsRoute";
import { Homepage } from "./pages/Homepage";
import { NotFoundPage } from "./pages/NotFoundPage";
import store from "./store";

const HotelsApp = lazy(() => import("./pages/Hotels"));
const TripsApp = lazy(() => import("./pages/Trips"));
const OffersApp = lazy(() => import("./pages/Offers"));

const slots: Slots = {};

const ExperimentWrapper = ({ children }: PropsWithChildren) => {
  const { sessionInfo } = useSessionContext();
  const isLoggedIn = useMemo(() => !!sessionInfo?.hopperUserId, [sessionInfo]);

  return (
    <ExperimentsProvider apiConfig={apiConfig} isLoggedIn={isLoggedIn}>
      {children}
    </ExperimentsProvider>
  );
};

export const App = () => {
  return (
    <Portal config={config} store={store}>
      <UserSourceProvider>
        <SessionContextProvider>
          <ExperimentWrapper>
            <AxiosInterceptorWrapper version={environmentConfig.version}>
              <AppTrackingProvider>
                <Routes>
                  <Route
                    path={PATH_AGENT_IMPERSONATION}
                    element={<AgentImpersonation />}
                  />
                  <Route
                    key={"home"}
                    path={"/"}
                    element={<Homepage funnel={Funnel.Ground} />}
                  />

                  {/* Hotels Routes */}
                  <Route
                    key={"home-hotels"}
                    path={`${HOTELS_PATH}`}
                    element={<Homepage funnel={Funnel.Lodging} />}
                  />
                  <Route
                    key={"hotels"}
                    path={`${HOTELS_PATH}/*`}
                    element={
                      <Suspense fallback={<LazyLoading />}>
                        <SlotProvider slots={slots}>
                          <HotelsApp />
                        </SlotProvider>
                      </Suspense>
                    }
                  />

                  {/* Trips Routes */}
                  <Route
                    path={"/trips/*"}
                    element={
                      <CommerceTripsRoute>
                        <Suspense fallback={<LazyLoading />}>
                          <TripsApp />
                        </Suspense>
                      </CommerceTripsRoute>
                    }
                  />

                  <Route
                    path={"/wallet/*"}
                    element={
                      <Suspense fallback={<LazyLoading />}>
                        <OffersApp />
                      </Suspense>
                    }
                  />

                  <Route
                    path={"/auth/startSession"}
                    element={<StartSessionComponent />}
                  />

                  <Route
                    path={"/auth/invalidSession"}
                    element={<InvalidSessionPage />}
                  />

                  {/* 404 Catch-all route */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </AppTrackingProvider>
            </AxiosInterceptorWrapper>
          </ExperimentWrapper>
        </SessionContextProvider>
      </UserSourceProvider>
    </Portal>
  );
};
